import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Moon, Sun, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "solucao", label: "Solução" },
  { id: "calculadora", label: "Calculadora" },
  { id: "como-funciona", label: "Como funciona" },
  { id: "segmentos", label: "Segmentos" },
  { id: "planos", label: "Planos" },
  { id: "faq", label: "FAQ" },
];

const Logo = ({ onClick }) => {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const update = () => setIsDark(document.documentElement.classList.contains("dark"));
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return (
    <button
      data-testid="nav-logo"
      onClick={onClick}
      className="group flex items-center"
      aria-label="CliniAtiva — voltar ao início"
    >
      <img
        src={isDark ? "/logo-dark.png" : "/logo-light.png"}
        alt="CliniAtiva — tecnologia"
        className="h-9 w-auto transition-opacity duration-300 group-hover:opacity-75"
      />
    </button>
  );
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);

  useEffect(() => {
    setDark(document.documentElement.classList.contains("dark"));
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    window.localStorage.setItem("cliniativa-theme", next ? "dark" : "light");
  };

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-6"
      >
        <nav
          className={`mx-auto flex h-16 max-w-5xl items-center justify-between gap-4 rounded-full border border-[var(--line)] bg-[var(--paper-2)]/85 px-5 backdrop-blur-xl transition-shadow duration-500 ${
            scrolled ? "shadow-[0_16px_44px_rgba(16,42,67,0.16)]" : "shadow-[0_8px_30px_rgba(16,42,67,0.08)]"
          }`}
        >
          <Logo onClick={() => go("inicio")} />

          <div className="hidden items-center gap-6 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="group relative text-[13px] font-semibold text-[var(--ink-2)] transition-colors duration-300 hover:text-[var(--brand)]"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--brand)] transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="theme-toggle-button"
              onClick={toggleTheme}
              aria-label="Alternar tema claro e escuro"
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] text-[var(--muted)] transition-colors duration-300 hover:border-[var(--brand)] hover:text-[var(--brand)]"
            >
              <motion.span
                key={dark ? "moon" : "sun"}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="block"
              >
                {dark ? <Moon className="h-4.5 w-4.5 h-[18px] w-[18px]" /> : <Sun className="h-[18px] w-[18px]" />}
              </motion.span>
            </button>

            <button
              data-testid="nav-cta-demo"
              onClick={() => go("demonstracao")}
              className="hidden rounded-full bg-turquoise px-5 py-2.5 text-sm font-semibold text-petrol transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_8px_24px_rgba(39,179,176,0.4)] sm:block"
            >
              Agendar demonstração
            </button>

            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Abrir menu"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] text-[var(--ink)] lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-petrol"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <img src="/logo-dark.png" alt="CliniAtiva" className="h-8 w-auto" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Fechar menu"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[{ id: "inicio", label: "Início" }, ...LINKS].map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.id)}
                  className="border-b border-white/10 py-4 text-left font-display text-3xl font-semibold text-white transition-colors duration-300 hover:text-turquoise"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-cta-demo"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                onClick={() => go("demonstracao")}
                className="mt-8 rounded-full bg-turquoise px-8 py-4 font-display text-lg font-bold text-petrol"
              >
                Agendar demonstração gratuita
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
